import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import Footer from '../../Footer/Footer';
import ImageSlantPricing from '../../common/image-slant-pricing';
import NewsletterSignUp from '../../common/Newsletter-sign';
import FestiveAfternoonTeaEntryModal from './FestiveAfternoonTeaEntryModal';
import imgGrotto from '../../../assets/christmas25/grotto.webp';
import imgSearch from '../../../assets/christmas25/search.webp';
import imgFestive from '../../../assets/christmas25/festive.webp';
import imgTea1 from '../../../assets/christmas25/tea1.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const heroImage =
  'https://BelvoirCastlePull.b-cdn.net/Website/Belvoir%20Castle%20Christmas-94.webp';

const imgCandlelight =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/Castle-by-Candlelight.webp';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
  accent:    '#9d6d49',
};

/* ── PAGE SHELL ── */
const Page = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

/* ── HERO ── */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(400px, 60vh, 640px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.55) 35%,
    rgba(16,29,64,0.1) 65%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroEyebrow = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.6rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.4rem, 5.5vw, 4.2rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1rem, 2vw, 1.3rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.8);
  max-width: 38rem;
`;

/* ── INTRO ── */
const IntroSection = styled.section`
  padding: clamp(2rem, 4.5vw, 3rem) clamp(1.25rem, 4vw, 2rem) clamp(1.25rem, 3vw, 1.75rem);
  border-top: 1px solid rgba(16,29,64,0.07);
  background: linear-gradient(180deg,
    ${props => props.theme.colors.offWhite} 0%,
    rgba(241,239,234,0.35) 100%
  );
`;

const IntroInner = styled.div`
  max-width: 42rem;
  margin: 0 auto;
  text-align: center;
`;

const IntroEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.48);
  margin: 0 0 0.65rem;
`;

const IntroTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.75rem, 4vw, 2.35rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.85rem;
  line-height: 1.12;
`;

const IntroSubtitle = styled.p`
  font-size: clamp(0.94rem, 1.85vw, 1.05rem);
  line-height: 1.6;
  color: rgba(16,29,64,0.58);
  margin: 0;
`;

/* ── TICKET TYPES (shared with DayTickets) ── */
const TicketTypesSection = styled.section`
  margin: 0;
  padding: clamp(2.5rem, 6vw, 3.75rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid rgba(16,29,64,0.07);
  background: linear-gradient(180deg,
    ${props => props.theme.colors.offWhite} 0%,
    rgba(241,239,234,0.35) 50%,
    ${props => props.theme.colors.offWhite} 100%
  );
`;

const TicketTypesInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

const TicketTypesHeader = styled.div`
  text-align: center;
  max-width: 36rem;
  margin: 0 auto clamp(1.75rem, 4vw, 2.35rem);
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
`;

const EyebrowLine = styled.span`
  display: block;
  width: 2rem;
  height: 1.5px;
  background: ${({ theme }) => theme.colors.accent};
  border-radius: 2px;
  opacity: 0.85;
`;

const EyebrowText = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.48);
`;

const TicketTypesTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  line-height: 1.15;

  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 2px;
    margin: 1rem auto 0;
    border-radius: 2px;
    background: linear-gradient(90deg,
      transparent,
      ${props => props.theme.colors.accent},
      transparent
    );
  }
`;

const TicketTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem 1.25rem;

  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const TicketTypeCard = styled.article`
  padding: 1.25rem 1.35rem;
  border-radius: 16px;
  border: 1px solid rgba(16,29,64,0.08);
  background: #fff;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.9) inset,
    0 10px 32px rgba(16,29,64,0.06);
`;

const TicketTypeLabel = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.05rem, 2vw, 1.2rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
  line-height: 1.2;
`;

const TicketTypeDetail = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.65vw, 0.95rem);
  line-height: 1.55;
  color: rgba(16,29,64,0.62);
  margin: 0;
`;

/* ── SUMMER LINK BANNER ── */
const SummerBanner = styled.section`
  background: linear-gradient(135deg, ${C.navy} 0%, #1a2d5a 100%);
  padding: clamp(2rem, 5vw, 2.75rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid rgba(255,255,255,0.06);
`;

const SummerBannerInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.75rem 2.5rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
`;

const SummerBannerAccent = styled.div`
  width: 3px;
  align-self: stretch;
  background: linear-gradient(180deg, ${C.gold} 0%, rgba(201,150,60,0.3) 100%);
  border-radius: 3px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 48px;
    height: 3px;
    align-self: auto;
  }
`;

const SummerBannerCopy = styled.div`
  flex: 1;
  min-width: 0;
`;

const SummerBannerEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.gold};
  margin: 0 0 0.5rem;
`;

const SummerBannerHeadline = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.2rem, 2.8vw, 1.6rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.55rem;
  line-height: 1.2;
`;

const SummerBannerSubtext = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.875rem, 1.7vw, 0.95rem);
  line-height: 1.6;
  color: rgba(255,255,255,0.7);
  margin: 0;
`;

const SummerBannerCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.85rem 1.65rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${C.navy};
  background: #fff;
  border: 2px solid #fff;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: ${C.gold};
    border-color: ${C.gold};
    color: #fff;
  }
`;

/* ── COMPONENT ── */
const ChristmasDayTickets = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const [showFestiveTeaEntryModal, setShowFestiveTeaEntryModal] = useState(false);
  const pageClassName = isBlackFriday
    ? 'christmas-page christmas-page--black-friday'
    : 'christmas-page';

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>Christmas Tickets at Belvoir Castle 2026 | Deck the Halls & More</title>
        <meta
          name="description"
          content="Book your Christmas tickets at Belvoir Castle — Deck the Halls Castle entry, Santa's Grotto, Search for Santa, Castle by Candlelight and more. Pre-booking essential."
        />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/tickets" />
        <meta property="og:title" content="Christmas Tickets at Belvoir Castle 2026" />
        <meta property="og:description" content="Book your Christmas experience — Deck the Halls, Santa's Grotto, Search for Santa, Castle by Candlelight and more." />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/tickets" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Christmas Tickets at Belvoir Castle 2026" />
        <meta name="twitter:description" content="Deck the Halls, Santa's Grotto, Search for Santa, Castle by Candlelight — book your Christmas experience." />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      {/* ── HERO ── */}
      <HeroWrap>
        <HeroImg src={heroImage} alt="Christmas at Belvoir Castle" />
        <HeroOverlay />
        <HeroContent>
          <HeroEyebrow>Belvoir Castle · Christmas 2026</HeroEyebrow>
          <HeroTitle>
            Christmas <em>Tickets</em>
          </HeroTitle>
          <HeroStrap>
            Choose your festive experience — pre-booking is essential for all Christmas events
          </HeroStrap>
        </HeroContent>
      </HeroWrap>

      {/* ── INTRO ── */}
      <IntroSection>
        <IntroInner>
          <IntroEyebrow>Choose your experience</IntroEyebrow>
          <IntroTitle>Every way to experience Christmas at Belvoir</IntroTitle>
          <IntroSubtitle>
            From a full Deck the Halls Castle day to a magical evening by candlelight — browse all
            Christmas experiences below and book your place. Spaces are limited; pre-booking is essential.
          </IntroSubtitle>
        </IntroInner>
      </IntroSection>

      {/* ── DECK THE HALLS ── */}
      <ImageSlantPricing
        eyebrow="The main event"
        image={imgFestive}
        title="Deck the Halls — Castle Entry"
        description="This Christmas, Belvoir Castle transforms into a world of song and story. Our grand rooms are dressed in the spirit of beloved Christmas carols. Includes the Mice Trail and access to all Castle areas. Add Santa's Grotto, Festive Afternoon Tea or Search for Santa at checkout."
        buttonText="Book Deck the Halls"
        buttonAction={() => {
          window.location.href =
            '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';
        }}
        imagePosition="left"
      />

      {/* ── SEARCH FOR SANTA ── */}
      <ImageSlantPricing
        eyebrow="Selected December dates · 5pm – 7:30pm"
        image={imgSearch}
        title="Search for Santa"
        description="An exclusive late afternoon family trail through the Castle to find Santa. Children enjoy hot chocolate, a biscuit and a present; parents get refreshments and a glass of bubbles. Full-day Castle access included. 5, 6, 12, 13, 19, 20, 21 & 23 December 2026."
        buttonText="Book Search for Santa"
        buttonAction={() => {
          window.location.href =
            '/booking-journey?locationId=978&pageId=d771a85f-632e-4a25-8651-c3a60fb31a10';
        }}
        imagePosition="right"
      />

      {/* ── SANTA'S GROTTO (CASTLE) ── */}
      <ImageSlantPricing
        eyebrow="Add-on to Castle entry"
        image={imgGrotto}
        title="Santa's Grotto — Castle"
        description="A private, unhurried visit with Father Christmas inside Belvoir Castle, during Deck the Halls. Children's tickets only required — accompanying adults do not need a Grotto ticket. Every child receives a special present. Add to your Castle entry ticket at checkout and choose your time slot."
        buttonText="Book Castle Grotto"
        buttonAction={() => {
          window.location.href =
            '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';
        }}
        imagePosition="left"
      />

      {/* ── SANTA'S GROTTO (RETAIL VILLAGE) ── */}
      <ImageSlantPricing
        eyebrow="No Castle entry required"
        image={imgGrotto}
        title="Santa's Grotto — Retail Village"
        description="Meet Father Christmas in his magical grotto at Belvoir Retail Village — a heartwarming family visit with a present for every child. Perfect on its own or combined with a Castle day out. No Castle entry ticket required."
        buttonText="Book Village Grotto"
        buttonAction={() => {
          window.location.href =
            '/booking-journey?locationId=978&pageId=d4314a7f-aa55-4e17-994e-f58655da7072';
        }}
        imagePosition="right"
      />

      {/* ── CASTLE BY CANDLELIGHT ── */}
      <ImageSlantPricing
        eyebrow="Every Thursday in December · 6pm – 8.30pm"
        image={imgCandlelight}
        title="Castle by Candlelight"
        description="Deck the Halls by candlelight — the Castle's grand rooms glowing in the warmth of a winter's evening. Includes bubbles and a mince pie on arrival in the Guard Room, live musicians throughout, and free-flow drinks all evening."
        buttonText="Find Out More"
        buttonAction={() => {
          window.location.href = '/christmas/castle-by-candlelight';
        }}
        imagePosition="left"
      />

      {/* ── FESTIVE AFTERNOON TEA ── */}
      <ImageSlantPricing
        eyebrow="Add-on to Castle or Gardens entry"
        image={imgTea1}
        title="Festive Afternoon Tea"
        description="Indulge in a lavish Festive Afternoon Tea in the Aviary Tearoom — seasonal savouries, festive patisserie and fine teas. Choose Castle entry or Gardens & Playground entry, then add tea at checkout. Available 14 November – 30 December 2026."
        buttonText="Book Afternoon Tea"
        buttonAction={() => {
          setShowFestiveTeaEntryModal(true);
        }}
        imagePosition="right"
      />

      {/* ── TICKET TYPES ── */}
      <TicketTypesSection aria-labelledby="ticket-types-heading">
        <TicketTypesInner>
          <TicketTypesHeader>
            <EyebrowRow>
              <EyebrowLine aria-hidden />
              <EyebrowText>Before you book</EyebrowText>
              <EyebrowLine aria-hidden />
            </EyebrowRow>
            <TicketTypesTitle id="ticket-types-heading">Ticket types</TicketTypesTitle>
          </TicketTypesHeader>
          <TicketTypesGrid>
            <TicketTypeCard>
              <TicketTypeLabel>Adult</TicketTypeLabel>
              <TicketTypeDetail>18+</TicketTypeDetail>
            </TicketTypeCard>
            <TicketTypeCard>
              <TicketTypeLabel>Child</TicketTypeLabel>
              <TicketTypeDetail>3–17</TicketTypeDetail>
            </TicketTypeCard>
            <TicketTypeCard>
              <TicketTypeLabel>Under 3</TicketTypeLabel>
              <TicketTypeDetail>0–2 (free)</TicketTypeDetail>
            </TicketTypeCard>
            <TicketTypeCard>
              <TicketTypeLabel>Concession</TicketTypeLabel>
              <TicketTypeDetail>65+ and students with valid ID</TicketTypeDetail>
            </TicketTypeCard>
          </TicketTypesGrid>
        </TicketTypesInner>
      </TicketTypesSection>

      {/* ── NEWSLETTER ── */}
      <NewsletterSignUp tag="Christmas 2026" />

      {/* ── SUMMER / ALL-YEAR LINK ── */}
      <SummerBanner>
        <SummerBannerInner>
          <SummerBannerAccent aria-hidden />
          <SummerBannerCopy>
            <SummerBannerEyebrow>Not just at Christmas</SummerBannerEyebrow>
            <SummerBannerHeadline>Visiting Belvoir outside of the festive season?</SummerBannerHeadline>
            <SummerBannerSubtext>
              Explore all day-ticket options — Castle, gardens, playground, animal park
              and more — on our main tickets page.
            </SummerBannerSubtext>
          </SummerBannerCopy>
          <SummerBannerCTA to="/your-visit/day-tickets">
            View all day tickets
          </SummerBannerCTA>
        </SummerBannerInner>
      </SummerBanner>

      <Footer />

      <FestiveAfternoonTeaEntryModal
        open={showFestiveTeaEntryModal}
        onClose={() => setShowFestiveTeaEntryModal(false)}
      />
    </Page>
  );
};

export default ChristmasDayTickets;
